import { useState } from "react";
import { Plus, Trash, ArrowUp, ArrowDown, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { EnrollmentQuestion } from "@/types/enrollment-questionnaire";

interface Draft {
  id: string;
  prompt: string;
  isNew: boolean;
}

interface Props {
  initialQuestions: EnrollmentQuestion[];
  isSaving: boolean;
  onSave: (questions: { id?: string; prompt: string }[]) => void;
}

const MAX_QUESTIONS = 30;
const MAX_PROMPT_LEN = 500;

function toDraft(q: EnrollmentQuestion): Draft {
  return { id: q.id, prompt: q.prompt, isNew: false };
}

export function QuestionnaireEditor({
  initialQuestions,
  isSaving,
  onSave,
}: Props) {
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    initialQuestions.map(toDraft)
  );

  function update(idx: number, prompt: string) {
    setDrafts((d) => d.map((q, i) => (i === idx ? { ...q, prompt } : q)));
  }
  function add() {
    setDrafts((d) => [
      ...d,
      { id: crypto.randomUUID(), prompt: "", isNew: true },
    ]);
  }
  function remove(idx: number) {
    setDrafts((d) => d.filter((_, i) => i !== idx));
  }
  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= drafts.length) return;
    setDrafts((d) => {
      const copy = [...d];
      [copy[idx], copy[target]] = [copy[target]!, copy[idx]!];
      return copy;
    });
  }

  function handleSave() {
    onSave(
      drafts
        .filter((d) => d.prompt.trim().length > 0)
        .map((d) =>
          d.isNew
            ? { prompt: d.prompt.trim() }
            : { id: d.id, prompt: d.prompt.trim() }
        )
    );
  }

  const hasInvalid = drafts.some((d) => d.prompt.trim().length === 0);
  const tooMany = drafts.length > MAX_QUESTIONS;

  if (drafts.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border/80 bg-secondary/20 px-6 py-14 text-center">
        <h3
          className="font-heading text-2xl font-medium tracking-tight"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Još nemate upitnik
        </h3>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          Dodajte prvo pitanje — terapeuti će ga vidjeti u koraku{" "}
          <span className="italic">Upitnik</span> tokom prijave.
        </p>
        <Button
          type="button"
          onClick={add}
          size="lg"
          className="mt-6 rounded-full px-5"
        >
          <Plus />
          Dodaj prvo pitanje
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {drafts.map((d, idx) => {
          const len = d.prompt.length;
          const remaining = MAX_PROMPT_LEN - len;
          return (
            <li
              key={d.id}
              className="animate-ink-rise"
              style={{ animationDelay: `${idx * 30}ms` }}
            >
              <article className="group relative overflow-hidden rounded-xl border border-border/70 bg-card p-5 transition-colors hover:border-primary/40">
                <span
                  aria-hidden
                  className="absolute inset-y-3 left-0 w-[2px] origin-bottom scale-y-0 rounded-r bg-primary transition-transform duration-300 ease-out group-hover:scale-y-100"
                />

                <div className="flex items-start gap-3 pl-2">
                  <span
                    className="mt-1 w-10 shrink-0 font-heading text-2xl font-medium italic tabular-nums leading-none text-primary/70 sm:text-3xl"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>

                  <div className="min-w-0 flex-1 space-y-2">
                    <Textarea
                      value={d.prompt}
                      maxLength={MAX_PROMPT_LEN}
                      onChange={(e) => update(idx, e.target.value)}
                      placeholder="Tekst pitanja…"
                      rows={2}
                      className="resize-none border-x-0 border-t-0 border-b border-border/70 bg-transparent px-0 py-1 text-base shadow-none focus-visible:border-primary focus-visible:ring-0"
                    />
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span
                        className="font-heading italic"
                        style={{ fontFamily: "var(--font-heading)" }}
                      >
                        № {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span
                        className={
                          remaining < 50
                            ? "tabular-nums text-amber-600"
                            : "tabular-nums"
                        }
                      >
                        {len}/{MAX_PROMPT_LEN}
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-center gap-0.5 opacity-60 transition-opacity group-hover:opacity-100">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      aria-label="Pomjeri gore"
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => move(idx, 1)}
                      disabled={idx === drafts.length - 1}
                      aria-label="Pomjeri dolje"
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => remove(idx)}
                      aria-label="Obriši pitanje"
                      className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash />
                    </Button>
                  </div>
                </div>
              </article>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-border/80 bg-secondary/30 px-3 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="ml-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Add
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={add}
            disabled={tooMany}
            className="rounded-full"
          >
            <Plus />
            Novo pitanje
          </Button>
          <span
            className="font-heading text-[11px] italic tabular-nums text-muted-foreground/70"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {drafts.length}/{MAX_QUESTIONS}
          </span>
        </div>

        <Button
          type="button"
          onClick={handleSave}
          size="lg"
          disabled={isSaving || hasInvalid || tooMany}
          className="rounded-full px-5"
        >
          <FloppyDisk />
          {isSaving ? "Spremanje…" : "Snimi"}
        </Button>
      </div>

      {tooMany && (
        <p className="text-xs text-rose-600">
          Maksimalno {MAX_QUESTIONS} pitanja.
        </p>
      )}
      {hasInvalid && !tooMany && (
        <p className="text-xs text-amber-600">
          Sva pitanja moraju imati tekst prije snimanja.
        </p>
      )}
    </div>
  );
}
